import type { MoneyRow } from './types';

export interface MergedMixRow {
  key: string;
  current: MoneyRow | null;
  previous: MoneyRow | null;
  currentUnavailable: boolean;
  sharePercent: number | null;
}

function groupKey(row: MoneyRow): string { return row.key || row.productId || row.store || ''; }

/**
 * Preserve prior-only groups so a channel or product disappearing in the
 * selected period is visible. Shares are display-only and are withheld for a
 * truncated or partial dimension; money arithmetic stays server-authoritative.
 */
export function mergeMixGroups(current: MoneyRow[], previous: MoneyRow[], truncated: boolean): MergedMixRow[] {
  const currentByKey = new Map(current.map((row) => [groupKey(row), row]));
  const previousByKey = new Map(previous.map((row) => [groupKey(row), row]));
  const keys = new Set([...currentByKey.keys(), ...previousByKey.keys()]);
  let total = 0;
  let complete = !truncated;
  for (const row of current) {
    if (row.grossCollections === null || row.coverage.grossKnown !== row.coverage.total) { complete = false; break; }
    const amount = Number(row.grossCollections);
    if (!Number.isFinite(amount)) { complete = false; break; }
    total += amount;
  }
  if (!Number.isFinite(total)) complete = false;
  return [...keys].map((key) => {
    const row = currentByKey.get(key) || null;
    const gross = row?.grossCollections;
    const amount = gross === null || gross === undefined ? null : Number(gross);
    return {
      key,
      current: row,
      previous: previousByKey.get(key) || null,
      currentUnavailable: !row && truncated,
      sharePercent: complete && amount !== null && Number.isFinite(amount) && total > 0 ? (amount / total) * 100 : null,
    };
  });
}
